/**
 * Platform admin Overview tab: platform stats and quick actions.
 */
import React from 'react';
import { TouchableOpacity, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { Text } from '@/components/ui/text';
import { AdminStatCard, ADMIN_COLORS } from '@/components/admin/AdminUI';
import { useTranslation } from '../../../../hooks/useTranslation';
import { ADMIN_DEBUG_ACTIONS_ENABLED } from '../../../../config/featureFlags';
import type { PlatformStats } from '../platformAdminTypes';
import type { PlatformQuickActions } from '../hooks/usePlatformQuickActions';

function StatsGrid({ stats }: { stats: PlatformStats }) {
  const { t } = useTranslation();
  return (
    <View className="flex-row flex-wrap mb-6" style={{ gap: 12 }}>
      <AdminStatCard
        icon="school"
        label={t('platformAdmin.totalSchools')}
        value={stats?.total_schools ?? 0}
        hint={`${stats?.active_schools ?? 0} ${t('platformAdmin.active')}`}
        tone="primary"
      />
      <AdminStatCard
        icon="group"
        label={t('platformAdmin.b2b2cStudents')}
        value={stats?.total_students ?? 0}
        tone="primary"
      />
      <AdminStatCard
        icon="person"
        label={t('platformAdmin.b2cUsers')}
        value={stats?.total_b2c_users ?? 0}
        tone="success"
      />
      <AdminStatCard
        icon="payment"
        label={t('platformAdmin.activeSubscriptions')}
        value={stats?.active_subscriptions ?? 0}
        tone="warning"
      />
    </View>
  );
}

type ActionTone = 'primary' | 'success' | 'warning' | 'error' | 'purple';

const TONE_PALETTES: Record<ActionTone, { bg: string; fg: string }> = {
  primary: { bg: ADMIN_COLORS.primarySoft, fg: ADMIN_COLORS.primary },
  success: { bg: ADMIN_COLORS.successSoft, fg: ADMIN_COLORS.successText },
  warning: { bg: ADMIN_COLORS.warningSoft, fg: ADMIN_COLORS.warningText },
  error: { bg: ADMIN_COLORS.errorSoft, fg: ADMIN_COLORS.errorText },
  purple: { bg: '#EDE9FE', fg: '#6D28D9' },
};

// Quick Actions — soft pastel icon blocks, calmer card chrome.
function QuickActions({ actions: quickActions }: { actions: PlatformQuickActions }) {
  const { t } = useTranslation();

  const actions: {
    icon: keyof typeof MaterialIcons.glyphMap;
    title: string;
    description: string;
    tone: ActionTone;
    onPress: () => void;
  }[] = [
    {
      icon: 'add-business',
      title: t('platformAdmin.createNewSchool'),
      description: t('platformAdmin.addSchool'),
      tone: 'primary',
      onPress: quickActions.createSchool,
    },
    {
      icon: 'folder',
      title: t('platformAdmin.manageB2CDocuments'),
      description: t('platformAdmin.uploadGeneral'),
      tone: 'primary',
      onPress: quickActions.manageB2CDocuments,
    },
    // ADM-7: these endpoints 404 on production, so hide the buttons there.
    ...(ADMIN_DEBUG_ACTIONS_ENABLED
      ? [
          {
            icon: 'science' as const,
            title: t('platformAdmin.createTestSchool'),
            description: t('platformAdmin.testB2B2C'),
            tone: 'purple' as const,
            onPress: quickActions.createTestSchool,
          },
          {
            icon: 'bug-report' as const,
            title: t('platformAdmin.debugAuth'),
            description: t('platformAdmin.testAuth'),
            tone: 'warning' as const,
            onPress: quickActions.debugAuth,
          },
        ]
      : []),
    {
      icon: 'error',
      title: t('platformAdmin.testSentry'),
      description: t('platformAdmin.testErrorMonitoring'),
      tone: 'error',
      onPress: quickActions.testSentry,
    },
  ];

  return (
    <View style={{ gap: 10 }}>
      {actions.map((action, index) => {
        const palette = TONE_PALETTES[action.tone];
        return (
          <TouchableOpacity
            key={index}
            onPress={action.onPress}
            activeOpacity={0.85}
            style={{
              backgroundColor: ADMIN_COLORS.background,
              borderWidth: 1,
              borderColor: ADMIN_COLORS.border,
              borderRadius: 16,
              padding: 14,
              flexDirection: 'row',
              alignItems: 'center',
              gap: 14,
            }}
          >
            <View
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                backgroundColor: palette.bg,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <MaterialIcons name={action.icon} size={20} color={palette.fg} />
            </View>
            <View style={{ flex: 1 }}>
              <Text
                className="text-sm font-semibold"
                style={{ color: ADMIN_COLORS.text.primary }}
              >
                {action.title}
              </Text>
              <Text
                className="text-xs"
                style={{ color: ADMIN_COLORS.text.secondary, marginTop: 2 }}
              >
                {action.description}
              </Text>
            </View>
            <MaterialIcons name="chevron-right" size={20} color={ADMIN_COLORS.text.muted} />
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

interface OverviewTabProps {
  stats: PlatformStats | null;
  quickActions: PlatformQuickActions;
}

export default function OverviewTab({ stats, quickActions }: OverviewTabProps) {
  const { t } = useTranslation();
  return (
    <View>
      <Text className="text-xl font-bold text-foreground mb-4">
        {t('platformAdmin.platformOverview')}
      </Text>
      {stats ? <StatsGrid stats={stats} /> : (
        <Text className="text-base text-muted-foreground italic text-center my-8">
          {t('common.loading')}
        </Text>
      )}
      <Text className="text-lg font-bold text-foreground mb-4">
        {t('platformAdmin.quickActions')}
      </Text>
      <QuickActions actions={quickActions} />
    </View>
  );
}
